// src/layouts/AdminLayout.js
import "../admin-theme.css";
import { NavLink, Outlet } from "react-router-dom";
import { FiLogOut, FiMenu } from "react-icons/fi";
import {
  Users,
  Building2,
  FileText,
  Settings,
  Bell,
  RefreshCw,
  Globe,
  Map,
  Landmark,
  LayoutDashboard,
  Mail,
  Megaphone,
  BarChart3,
} from "lucide-react";
import NotificationsDropdown from "../components/common/NotificationsDropdown";
import ScrollToTop from "../components/common/ScrollToTop";
import { useAuth } from "../context/AuthContext";
import { MainCompanyProvider, useMainCompany } from "../context/MainCompanyContext";
import { useRoleTheme } from "../context/RoleThemeContext";
import { useState, useRef, useEffect, useCallback } from "react";
import IdleSessionWatcher from "../components/session/IdleSessionWatcher";
import config from "../config/config";

function HeaderRightControls() {
  const { mainCompanies, selectedMain, setSelectedMain } = useMainCompany();
  const { roleTheme } = useRoleTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [btnHover, setBtnHover] = useState(false);

  //  console.log("HeaderRightControls: mainCompanies:", mainCompanies.map(c => ({id: c.id, name: c.main_company_name})), "selectedMain:", selectedMain);

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const selected = mainCompanies.find((m) => m.id === selectedMain);
  // console.log("HeaderRightControls: selected company found:", selected ? selected.main_company_name : "NOT FOUND");

  return (
    <div className="flex items-center gap-3" ref={ref}>
      <div className="relative">
        <button
          id="dropdownDefaultButton"
          onClick={() => setOpen((s) => !s)}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          type="button"
          className="text-white font-medium rounded-lg text-sm px-3 py-2 text-center inline-flex items-center leading-none w-60 justify-between focus:ring-4 focus:outline-none focus:ring-white/30"
          style={{
            backgroundColor: btnHover ? (roleTheme?.colors?.sidebar?.hover || '#2563eb') : (roleTheme?.colors?.sidebar?.active || '#1d4ed8')
          }}
          aria-expanded={open}
          aria-haspopup="menu"
        >

          <span className="truncate mr-2">{selected ? selected.main_company_name : 'Select Main Company'}</span>
          <svg className="w-3 h-3 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
          </svg>
        </button>



        <div id="dropdown" className={`${open ? '' : 'hidden'} z-10 absolute left-0 mt-2 bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-60`} role="menu" aria-labelledby="dropdownDefaultButton">
          <ul className="py-2 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
            {mainCompanies.length === 0 && (
              <li className="px-4 py-2 text-gray-400">No companies</li>
            )}
            {mainCompanies.map((m) => (
              <li key={m.id}>
                <button
                  onClick={() => { setSelectedMain(m.id); setOpen(false); }}
                  className="w-full text-left block px-4 py-2 hover:bg-gray-100"
                  role="menuitem"
                >
                  {m.main_company_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Helper function to convert to camel case (capitalize first letter of each word)
const toCamelCase = (str) => {
  if (!str) return '';
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export default function AdminLayout() {
  const { logoutAdmin, logoutUser, adminUser } = useAuth();
  const { roleTheme } = useRoleTheme();
  const [collapsed, setCollapsed] = useState(false);

  // Auto-collapse on mobile screens
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      //   console.log("AdminLayout: Screen size check - width:", window.innerWidth, "isMobile:", isMobile);
      if (isMobile) {
        //   console.log("AdminLayout: Auto-collapsing sidebar for mobile");
        setCollapsed(true);
      }
    };

    // Check on mount
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle nav link clicks - collapse sidebar on mobile
  const handleNavClick = () => {
    setCollapsed(false);
    setTimeout(() => {
      setCollapsed(true);
    }, 1000);
  };

  const handleLogout = async () => {
    await logoutAdmin();
    await logoutUser();
    const baseUrl = window.location.origin;

    if (adminUser?.userType === "M") {
      window.location.href = `${baseUrl}/login`;
    } else {
      window.location.href = `${baseUrl}/login`;
    }
  };

  const handleIdleTimeout = useCallback(async () => {
    await logoutAdmin();
    await logoutUser();
    window.location.href = `${window.location.origin}/login`;
  }, [logoutAdmin, logoutUser]);

  const navItems = [
    { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", collapsedLabel: "Dash", roles: ["M", "A"], userTypes: ["M", "A"] },
    { to: "/admin/main-company", icon: Building2, label: "Main Company", collapsedLabel: "Main", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/users", icon: Users, label: "Users", collapsedLabel: "Users", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/sales-reps", icon: Users, label: "Sales Reps", collapsedLabel: "Sales", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/accounts", icon: Users, label: "All Accounts", collapsedLabel: "All", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/company-settings", icon: Settings, label: "Company Settings", collapsedLabel: "Sett", roles: ["M", "A"], userTypes: ["M", "A"] },
    { to: "/admin/global-template", icon: FileText, label: "Templates", collapsedLabel: "Glob", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/national-settings", icon: Globe, label: "National", collapsedLabel: "Nati", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/state-settings", icon: Map, label: "States", collapsedLabel: "State", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/county-settings", icon: Landmark, label: "Counties", collapsedLabel: "Count", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/notify-users", icon: Bell, label: "Notifications", collapsedLabel: "Notif", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/transfer-titlerep", icon: RefreshCw, label: "Transfers", collapsedLabel: "Tranf", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/tracking-summary", icon: BarChart3, label: "Tracking", collapsedLabel: "Track", roles: ["A", "M"], userTypes: ["A", "M"] },
    //  { to: "/admin/updates", icon: Sparkles, label: "Updates & Features" },
    { to: "/admin/email-templates", icon: Mail, label: "Email Templates", collapsedLabel: "Email", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/dashboard-options", icon: LayoutDashboard, label: "Dashboard Options", collapsedLabel: "DashOpt", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/themes/master", icon: FileText, label: "Themes", collapsedLabel: "Them", roles: ["A"], userTypes: ["A"] },
    { to: "/admin/cms", icon: FileText, label: "CMS", collapsedLabel: "CMS", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/disclaimer", icon: FileText, label: "Disclaimer", collapsedLabel: "Disc", roles: ["A", "M"], userTypes: ["A", "M"] },
    { to: "/admin/advertise", icon: Megaphone, label: "Advert", collapsedLabel: "Adve", roles: ["A"], userTypes: ["A"] },
    //  { to: "/admin/app-version", icon: MonitorSmartphone, label: "App Version" },
  ];

  // Filter navigation items based on userType
  const filteredNavItems = adminUser?.userType === "A"
    ? navItems // Admin users see all items
    : navItems.filter(item => item.userTypes?.includes("M")); // Master users see only items allowed for "M"

  // Modify navigation paths for master users to use /master prefix
  const getNavPath = (to) => {
    if (adminUser?.userType === "M") {
      // For master users, convert /admin prefix to /master prefix for specific allowed routes
      const masterAllowedRoutes = ["/admin/dashboard", "/admin/change-password", "/admin/users", "/admin/tracking-summary", "/admin/company-settings", "/admin/sales-reps", "/admin/notify-users", "/admin/dashboard-options", "/admin/cms", "/admin/disclaimer"];
      if (masterAllowedRoutes.includes(to)) {
        return to.replace("/admin", "/master");
      }
    }
    return to;
  };

  return (
    <>
      <IdleSessionWatcher
        enabled={Boolean(adminUser)}
        idleMsBeforeWarning={config.idlePromptAfterMs}
        warningGraceMs={config.idlePromptGraceMs}
        onTimeout={handleIdleTimeout}
        onSignOut={handleLogout}
        timerName="ibottomline-admin"
      />
      <div className="min-h-screen bg-gray-100 flex">
        {/* Sidebar */}
        <aside
          className={`${collapsed ? "w-24" : "w-64"} text-white flex flex-col transition-all duration-300 admin-sidebar`}
          style={{
            background: roleTheme?.colors.sidebar.background || 'rgb(46 59 97)',
            backgroundSize: 'cover',
            '--sidebar-hover': roleTheme?.colors?.sidebar?.hover || 'rgb(59 73 113)',
            '--sidebar-active': roleTheme?.colors?.sidebar?.active || '#1d4ed8'
          }}
        >
          {/* Header with collapse button */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            {!collapsed && <span className="text-xl font-bold">
              {adminUser?.userType === "M" ? "Account Settings" : "Admin Panel"}
            </span>}
            <button
              onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              <FiMenu className="w-6 h-6" />
            </button>
          </div>

          {/* Admin Info: always show compact avatar; show full name when expanded */}
          {adminUser && (
            <div className="px-4 py-4 border-b border-white/10">
              <div className="flex items-center">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-semibold"
                  style={{ backgroundColor: 'rgba(255,255,255,0.95)', color: roleTheme?.colors?.sidebar?.active || '#1e3a8a' }}
                  title={`${toCamelCase(adminUser.first_name) || ''} ${toCamelCase(adminUser.last_name) || ''}`}
                  aria-label="admin-name"
                >
                  {(adminUser.first_name?.[0] || adminUser.email?.[0] || 'A').toUpperCase()}
                </div>

                {!collapsed && (
                  <div className="ml-3">
                    <p className="text-sm">Hi,</p>
                    <p className="font-semibold">
                      {toCamelCase(adminUser.first_name)} {toCamelCase(adminUser.last_name)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nav Links */}
          <nav className="flex-1 px-2 py-4 space-y-1">
            {filteredNavItems.map(({ to, icon: Icon, label, collapsedLabel }) => {
              // Define full descriptive names for tooltips
              const tooltipMap = {
                "Dashboard": "Dashboard",
                "Users": "Manage Users",
                "Tracking": "Tracking Summary",
                "Companies": "Main Company",
                "Company": "Company Settings",
                "Notifications": "Notify Users",
                "Transfers": "Transfer TitleRep",
                "Templates": "Global Templates",
                "National": "National Settings",
                "States": "State Settings",
                "Counties": "County Settings",
                "CMS": "Manage CMS",
                "Disclaimer": "Manage Disclaimer",
                "Ads": "Advertising",
                "Email Templates": "Email Templates",
                "Sales Reps": "Sales Representatives",
                "All Accounts": "Users & Sales Reps (combined)",
                "Themes": "Theme Setup"
              };

              return (
                <NavLink
                  key={to}
                  to={getNavPath(to)}
                  onClick={handleNavClick}
                  title={collapsed ? (tooltipMap[label] || label) : ""}
                  className={({ isActive }) => /* eslint-disable-line no-unused-vars */
                    `flex items-center ${collapsed ? 'gap-1' : 'gap-2'} p-3 rounded-lg transition-all duration-200 ease-in-out transform group ${isActive
                      ? 'text-white shadow-lg scale-[1.02]'
                      : 'text-gray-300 hover:text-white hover:shadow-md hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-offset-2'
                    }`
                  }
                  style={({ isActive }) => ({
                    backgroundColor: isActive ? (roleTheme?.colors.sidebar.active || '#1d4ed8') : 'transparent'
                  })}
                  onMouseEnter={(e) => { const a = e.currentTarget; if (a.getAttribute('aria-current') !== 'page') a.style.backgroundColor = roleTheme?.colors?.sidebar?.hover || 'rgba(59,73,113,0.8)'; }}
                  onMouseLeave={(e) => { const a = e.currentTarget; if (a.getAttribute('aria-current') !== 'page') a.style.backgroundColor = 'transparent'; }}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`text-[10px] font-medium leading-tight ${collapsed ? 'block' : 'hidden'}`}>
                    {collapsedLabel || label}
                  </span>
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              );
            })}
          </nav>

          {/* Logout fixed at bottom 
        <div className="p-4 border-t" style={{ borderTopColor: 'rgb(46 59 97)' }}>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-600 px-3 py-2 rounded-lg w-full hover:bg-red-700 transition"
          >
            <FiLogOut className="w-4 h-4" />
            {!collapsed && "Logout"}
          </button>
        </div>*/}
        </aside>

        {/* Right panel: header + main content */}
        <div className="flex-1 flex flex-col">
          <MainCompanyProvider>
            <ScrollToTop />
            <header
              className="text-white flex items-center px-4 justify-end gap-3"
              style={{
                height: '40px',
                background: roleTheme?.colors.header.background || 'rgb(46 59 97)',
                backgroundSize: 'cover'
              }}
            >
              <HeaderRightControls />
              {adminUser?.userType === "A" && <NotificationsDropdown />}
              <button
                onClick={handleLogout}
                title="Logout"
                className="flex items-center gap-2 text-white hover:opacity-90"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </header>

            <main
              className="flex-1 p-2 overflow-auto bg-gray-100 admin-role-theme"
              style={{
                '--role-border-primary': roleTheme?.colors?.border?.primary || '#2563eb',
                '--role-border-light': roleTheme?.colors?.border?.light || '#bfdbfe'
              }}
            >
              <Outlet />
            </main>
          </MainCompanyProvider>
        </div>
      </div>
    </>
  );
}
